import { Box, Tooltip, TooltipProps, Typography, tooltipClasses } from '@mui/material'
import { styled } from '@mui/material/styles'
import React from 'react'
import { ErrorIcon, SlashIcon, SuccessIcon, WarningIcon } from 'src/Libs/flow/icons'
import { DeviceStatus } from 'src/Libs/flow/installation/api'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { DeviceError } from '../../utils'

interface DeviceStatusIconProps {
  status: DeviceStatus
  errors: DeviceError[]
}

/**
 * Given a device status it displays the appropriate status icon.
 */
export const DeviceStatusIcon: React.FC<DeviceStatusIconProps> = ({ status, errors }) => {
  const { t } = useCustomTranslation()

  const errorsWithMessages = errors.filter(error => !!error.key)

  switch (status) {
    case 'ok':
      return <SuccessIcon color="primary" />
    case 'warning':
      return (
        <WarningTooltip
          arrow
          placement="bottom-end"
          title={
            errorsWithMessages.length ? (
              <>
                {errorsWithMessages.map(error => (
                  <Box key={error.key}>{t(error.key)}</Box>
                ))}
              </>
            ) : undefined
          }
        >
          <Box component={'span'}>
            <WarningIcon color="warning" />
          </Box>
        </WarningTooltip>
      )

    case 'error':
      return (
        <ErrorTooltip
          arrow
          placement="bottom-end"
          title={
            errorsWithMessages.length ? (
              <>
                {errorsWithMessages.map(error => (
                  <Box
                    key={error.key}
                    bgcolor={theme => (error.type === 'Warning' ? theme.palette.warning.main : undefined)}
                    py={0.5}
                    px={1}
                    sx={{
                      '&:first-of-type': {
                        borderTopLeftRadius: 4,
                        borderTopRightRadius: 4,
                      },
                      '&:last-of-type': {
                        borderBottomLeftRadius: 4,
                        borderBottomRightRadius: 4,
                      },
                    }}
                  >
                    {t(error.key)}
                  </Box>
                ))}
              </>
            ) : undefined
          }
        >
          <Box component={'span'}>
            <ErrorIcon color="error" />
          </Box>
        </ErrorTooltip>
      )

    default:
      return (
        <Tooltip title={t('installation.deviceStatus.unknownTooltip')} arrow placement="bottom-end">
          <Box component={'span'}>
            <SlashIcon />
          </Box>
        </Tooltip>
      )
  }
}

/**
 * Given a device status it displays a dot with the appropriate color along with the status as text.
 */
export const DeviceStatusIconMobile: React.FC<DeviceStatusIconProps> = ({ status, errors }) => {
  const { t } = useCustomTranslation()

  if (status === 'ok') {
    return (
      <Typography variant="caption">
        <Box component={'span'} color={theme => theme.palette.success.main}>
          ⬤
        </Box>{' '}
        {t('installation.deviceStatus.ok')}
      </Typography>
    )
  }

  const errorsWithMessages = errors.filter(error => !!error.key)

  /** If there are warnings but they can not be displayed (because of the user role) show a generic warning message. */
  if (status === 'warning' && errorsWithMessages.length === 0) {
    return (
      <Typography variant="caption">
        <Box component={'span'} color={theme => theme.palette.warning.main}>
          ⬤
        </Box>{' '}
        {t('installation.deviceStatus.warning')}
      </Typography>
    )
  }

  /** If there are errors but they can not be displayed (because of the user role) show a generic warning message. */
  if (status === 'error' && errorsWithMessages.length === 0) {
    return (
      <Typography variant="caption">
        <Box component={'span'} color={theme => theme.palette.error.main}>
          ⬤
        </Box>{' '}
        {t('installation.deviceStatus.error')}
      </Typography>
    )
  }

  /** There are errors and/or warning and they can be displayed. */
  if ((status === 'error' || status === 'warning') && errors.length > 0) {
    return (
      <>
        {errors.map(error => {
          return (
            <Box key={error.key}>
              <Typography variant="caption" sx={{ wordBreak: 'break-all' }}>
                <Box component={'span'} color={theme => (error.type === 'Error' ? theme.palette.error.main : theme.palette.warning.main)}>
                  ⬤
                </Box>{' '}
                {t(error.key)}
              </Typography>
            </Box>
          )
        })}
      </>
    )
  }
  // eslint-disable-next-line @typescript-eslint/switch-exhaustiveness-check
  switch (status) {
    case 'warning':
      return (
        <Typography variant="caption">
          <Box component={'span'} color={theme => theme.palette.warning.main}>
            ⬤
          </Box>
          {t('installation.deviceStatus.warning')}
        </Typography>
      )
    case 'error':
      return (
        <Typography variant="caption">
          <Box component={'span'} color={theme => theme.palette.error.main}>
            ⬤
          </Box>{' '}
          {t('installation.deviceStatus.error')}
        </Typography>
      )
  }

  return (
    <Typography variant="caption">
      <Box component={'span'} color={theme => theme.palette.divider}>
        ⬤
      </Box>{' '}
      {t('installation.deviceStatus.unknown')}
    </Typography>
  )
}

const WarningTooltip = styled(({ className, ...props }: TooltipProps) => <Tooltip {...props} classes={{ popper: className }} />)(({ theme }) => ({
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: theme.palette.warning.main,
    color: theme.palette.common.white,
  },
  [`& .${tooltipClasses.arrow}`]: {
    color: theme.palette.warning.main,
  },
}))

const ErrorTooltip = styled(({ className, ...props }: TooltipProps) => <Tooltip {...props} classes={{ popper: className }} />)(({ theme }) => ({
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: theme.palette.error.main,
    color: theme.palette.common.white,
    /** padding is handled by the childred elements */
    padding: 0,
  },
  [`& .${tooltipClasses.arrow}`]: {
    color: theme.palette.error.main,
  },
}))
